/**
 * Helpers de presentación del dominio Pedidos de láminas (formato de fechas y
 * referencias de material).
 *
 * Puro formato para la UI; sin lógica de negocio ni acceso a la API. Las fechas
 * llegan del backend ya en ISO/local Colombia (serializer `_iso`), así que aquí
 * solo se acortan para mostrarlas; no se reinterpreta la zona.
 */
import { ApiError } from "@/lib/api";

import { PEDIDO_ERROR_MESSAGE } from "./constants";
import type { PedidoListItem } from "./types";

/**
 * Acorta un ISO datetime a "DD/MM HH:MM" (como el SSR: `strftime('%d/%m %H:%M')`).
 * Devuelve "—" si el valor es nulo/vacío.
 */
export function formatDateTimeShort(value: string | null): string {
  if (!value) {
    return "—";
  }
  const [date, time] = value.split("T");
  const [, month, day] = date.split("-");
  if (!day || !month) {
    return date;
  }
  const hhmm = time ? ` ${time.slice(0, 5)}` : "";
  return `${day}/${month}${hhmm}`;
}

/**
 * Formatea una fecha a "DD/MM/YYYY" (como el SSR en el historial:
 * `strftime('%d/%m/%Y')`). Devuelve "—" si es nula.
 */
export function formatDate(value: string | null): string {
  if (!value) {
    return "—";
  }
  const [date] = value.split("T");
  const [year, month, day] = date.split("-");
  if (!day || !month || !year) {
    return date;
  }
  return `${day}/${month}/${year}`;
}

/**
 * Traduce un error de la API a un mensaje legible para el usuario.
 *
 * El backend de pedidos devuelve en `message` el texto humano EXACTO del service
 * (p. ej. "Solo se pueden enviar pedidos pendientes"), por lo que se prefiere ese
 * mensaje; el `PEDIDO_ERROR_MESSAGE` (por `error_code`) solo es un respaldo cuando
 * el backend no trae mensaje. Lo comparten el diálogo de acciones y el formulario.
 */
export function pedidoErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return (
      error.message ||
      PEDIDO_ERROR_MESSAGE[error.error_code] ||
      "No se pudo completar la acción."
    );
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "No se pudo completar la acción.";
}

/**
 * Construye el texto de referencias de un pedido (calibre / medida / tubo), como
 * los "pills" del SSR. Devuelve un array de etiquetas no vacías para pintarlas.
 */
export function referenciasDePedido(pedido: PedidoListItem): string[] {
  const refs: string[] = [];
  if (pedido.calibre) {
    refs.push(`Cal. ${pedido.calibre}`);
  }
  if (pedido.medida) {
    refs.push(pedido.medida);
  }
  if (pedido.tubo) {
    refs.push(`Tubo ${pedido.tubo}`);
  }
  return refs;
}
